<script setup lang="ts">
  import { computed, watch } from 'vue'
  import { useTenantUserDetailContext } from '../composables/useTenantUserDetail'
  import {
    useTenantUserAccessRolesUtils,
    type AgencyStructureWithAccessRoles
  } from '../composables/useTenantUserAccessRolesUtils'
  import { useTenantUserRoleAssignments } from '../components/providers'
  import { useTenantUserDatabaseAccessGrants } from '../components/providers'
  import { useAgencyStructureContext } from '@gat-ship/agency'
  import { useAccessRolesCollectionContext } from '@gat-ship/access-control'
  import { useTenantUserAccessRolesLayout } from '../components/layouts/TenantUserAccessRolesLayout.vue'
  import {
    BadgeRemovable,
    ContentRowTwoColumns,
    useModalStack
  } from '@gat-ship/vue-components'
  import RoleAssignmentDropdown from '../components/dropdowns/RoleAssignmentDropdown.vue'
  import { Separator } from '@gat-ship/vue-components'
  import { Badge } from '@gat-ship/vue-components'
  const props = defineProps<{ id?: string }>()

  const layoutContext = useTenantUserAccessRolesLayout()

  const { roleAssignments, commands } = useTenantUserRoleAssignments()
  const { fetchData: fetchDatabaseAccessGrants } =
    useTenantUserDatabaseAccessGrants()
  const agencyStructureDataSource = useAgencyStructureContext()!
  const accessRolesDataSource = useAccessRolesCollectionContext()!
  const { TenantUserAccessRolesUtils } = useTenantUserAccessRolesUtils()

  watch(
    [() => layoutContext.selectedDatabase.value, () => props.id],
    ([dbId, userId]) => {
      if (dbId && userId) {
        accessRolesDataSource.fetchAccessRoles({ forceFetch: true })
        fetchDatabaseAccessGrants(userId, { forceFetch: true })
        agencyStructureDataSource.fetchAgencyStructure({
          forceFetch: true
        })
      }
    }
  )
  const agencyStructureWithAccessRoles = computed(
    (): AgencyStructureWithAccessRoles[] => {
      return TenantUserAccessRolesUtils.createAgencyStructureWithAccessRoles(
        agencyStructureDataSource.agencyStructure.value,
        accessRolesDataSource.accessRoles.value,
        roleAssignments.value
      )
    }
  )

  const tenantUserProvider = useTenantUserDetailContext()!

  const tenantUser = computed(() => {
    if (!props.id) {
      return undefined
    }
    return tenantUserProvider.tenantUser.value
  })

  function hasNoRoles(
    agencyStructureWithAccessRole: AgencyStructureWithAccessRoles
  ) {
    return TenantUserAccessRolesUtils.hasNoAccessRoles(
      agencyStructureWithAccessRole
    )
  }
  const stack = useModalStack()
</script>

<template>
  <separator />
  <div v-if="tenantUser?.isTenantAdmin">No roles assigned.</div>
  <template v-else>
    <template
      v-for="agencyStructureWithAccessRole in agencyStructureWithAccessRoles"
      :key="agencyStructureWithAccessRole.agencyNodeId"
    >
      <ContentRowTwoColumns>
        <template #column-one>
          <div
            class="flex flex-col gap-2"
            :style="{
              paddingLeft: `${agencyStructureWithAccessRole.agencyNodeLevel * 16}px`
            }"
          >
            <span class="text-sm font-medium">
              {{ agencyStructureWithAccessRole.agencyNodeName }}
            </span>
            <span class="text-muted-foreground text-xs">
              {{ agencyStructureWithAccessRole.assignedAccessRoles.length }}
              roles assigned.
            </span>
            <span class="text-muted-foreground text-xs">
              {{ agencyStructureWithAccessRole.inheritedAccessRoles.length }}
              roles inherited.
            </span>
          </div>
        </template>
        <div class="flex flex-wrap items-center gap-2">
          <div
            v-if="hasNoRoles(agencyStructureWithAccessRole)"
            class="text-muted-foreground text-xs"
          >
            No roles assigned.
          </div>
          <template
            v-for="role in agencyStructureWithAccessRole.assignedAccessRoles"
            :key="role.roleId"
          >
            <BadgeRemovable
              v-if="tenantUser?.userId && role.roleAssignmentId"
              :label="role.roleName"
              @click:item="stack.open('access-role', { id: role.roleId })"
              @click:delete="
                commands.removeRoleAssignment({
                  userId: tenantUser.userId,
                  roleAssignmentId: role.roleAssignmentId,
                  commandId: ''
                })
              "
            />
          </template>
          <badge
            v-for="role in agencyStructureWithAccessRole.inheritedAccessRoles"
            :key="role.roleId"
            variant="outline"
            class="bg-muted h-8 px-2 py-1 text-xs opacity-60"
          >
            {{ role.roleName }}
          </badge>
        </div>
        <div class="w-fit">
          <RoleAssignmentDropdown
            :agency-node-id="agencyStructureWithAccessRole.agencyNodeId"
          />
        </div>
      </ContentRowTwoColumns>
    </template>
  </template>
</template>
